import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ThemeManagerService } from '../services/theme-manager';

export interface QrFieldItem {
  key: string;
  value: string;
}

@Component({
    selector: 'app-decode-qr',
    templateUrl: './decode-qr.component.html',
    styleUrls: ['./decode-qr.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DecodeQrComponent implements OnInit, OnDestroy {
  rawQrParam: string = '';
  decodedText: string = '';
  fields: QrFieldItem[] = [];
  reportCategoryId: string | number | null = null;
  labelId: string | number | null = null;
  reportName: string = '';
  filterByField: string = '';
  filterByValue: string = '';
  hasError: boolean = false;
  errorMessage: string = '';
  showRaw: boolean = false;
  copiedAll: boolean = false;
  copiedIndex: number | null = null;
  scannedAt: Date = new Date();

  _theme: string = 'enterprise';
  private sub: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private titleService: Title,
    private _themeManager: ThemeManagerService
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Item Details - BarCloud');

    if (this._themeManager && this._themeManager.themeChange) {
      this.sub.add(
        this._themeManager.themeChange.subscribe((theme: string) => {
          this._theme = theme || 'enterprise';
        })
      );
    }

    this.sub.add(
      this.route.queryParams.subscribe(params => {
        let qrcode = params['qrcode'] || params['QRCode'] || params['qr'] || '';
        const cat = params['cat'] || params['catId'] || params['categoryId'] || params['reportCategoryId'] || '';
        const labelId = params['labelId'] || params['LabelId'] || params['presetId'] || '';
        const name = params['name'] || params['reportName'] || params['labelName'] || '';
        const field = params['field'] || params['filterBy'] || params['filter'] || '';
        const val = params['val'] || params['value'] || '';
        
        // Fallback: Check window.location if router query param is empty
        if (!qrcode) {
          qrcode = this.extractQrFromUrl();
        }

        this.processQrData(qrcode, { cat, labelId, name, field, val });
      })
    );
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  private extractQrFromUrl(): string {
    const fullUrl = window.location.href;
    const qIndex = fullUrl.indexOf('qrcode=');
    if (qIndex !== -1) {
      let paramPart = fullUrl.substring(qIndex + 7);
      const ampIndex = paramPart.indexOf('&');
      if (ampIndex !== -1) {
        paramPart = paramPart.substring(0, ampIndex);
      }
      const hashIndex = paramPart.indexOf('#');
      if (hashIndex !== -1) {
        paramPart = paramPart.substring(0, hashIndex);
      }
      try {
        return decodeURIComponent(paramPart);
      } catch (e) {
        return paramPart;
      }
    }
    return '';
  }

  private processQrData(qrcode: string, queryContext?: any): void {
    this.rawQrParam = qrcode ? qrcode.trim() : '';
    this.fields = [];
    this.reportCategoryId = queryContext?.cat || null;
    this.labelId = queryContext?.labelId || null;
    this.reportName = queryContext?.name || '';
    this.filterByField = queryContext?.field || '';
    this.filterByValue = queryContext?.val || '';
    this.hasError = false;
    this.errorMessage = '';
    this.scannedAt = new Date();

    if (!this.rawQrParam && !this.filterByValue) {
      this.hasError = true;
      this.errorMessage = 'No QR Code data was detected in the URL. Please scan a valid label QR code.';
      return;
    }

    try {
      if (this.rawQrParam) {
        this.decodedText = this.safeBase64Decode(this.rawQrParam);
        if (!this.decodedText || !this.decodedText.trim()) {
          this.hasError = true;
          this.errorMessage = 'QR Code payload is empty or could not be decoded.';
          return;
        }

        // Try parsing as JSON payload with structured metadata
        let parsedJson: any = null;
        try {
          parsedJson = JSON.parse(this.decodedText);
        } catch (e) {
          parsedJson = null;
        }

        if (parsedJson && typeof parsedJson === 'object') {
          this.labelId = parsedJson.labelId || parsedJson.presetId || this.labelId;
          this.reportName = parsedJson.name || parsedJson.reportName || this.reportName;
          this.reportCategoryId = parsedJson.catId || parsedJson.categoryId || parsedJson.reportCategoryId || this.reportCategoryId;
          this.filterByField = parsedJson.filterBy || parsedJson.field || this.filterByField;
          this.filterByValue = parsedJson.val || parsedJson.value || this.filterByValue;
          const textToParse = parsedJson.data || parsedJson.text || '';
          if (textToParse) {
            this.fields = this.parseDecodedText(textToParse);
          } else if (parsedJson.fields && Array.isArray(parsedJson.fields)) {
            this.fields = parsedJson.fields;
          } else if (this.filterByField && this.filterByValue) {
            this.fields = [{ key: this.filterByField, value: this.filterByValue }];
          }
        } else {
          this.fields = this.parseDecodedText(this.decodedText);
        }
      }

      // Fallback: If fields is empty but we have filter field & value from URL query params
      if (this.fields.length === 0 && this.filterByField && this.filterByValue) {
        this.fields = [{ key: this.filterByField, value: this.filterByValue }];
      }

      // Ensure the filterBy primary field is visible if available and not in list
      if (this.filterByField && this.filterByValue && !this.fields.some(f => f.key.toLowerCase() === this.filterByField.toLowerCase())) {
        this.fields.unshift({ key: this.filterByField, value: this.filterByValue });
      }

      if (this.fields.length === 0) {
        this.hasError = true;
        this.errorMessage = 'Unable to parse field values from the decoded QR payload.';
      }
    } catch (err: any) {
      this.hasError = true;
      this.errorMessage = 'Failed to decode QR code data: ' + (err?.message || 'Invalid format');
    }
  }

  private safeBase64Decode(input: string): string {
    if (!input) return '';
    let cleaned = input.trim();

    // Strategy 1: Standard UTF-8 base64 decode
    try {
      return decodeURIComponent(escape(atob(cleaned)));
    } catch (e1) {
      // Strategy 2: Simple atob
      try {
        return atob(cleaned);
      } catch (e2) {
        // Strategy 3: Handle spaces replacing '+'
        try {
          const fixed = cleaned.replace(/ /g, '+');
          return decodeURIComponent(escape(atob(fixed)));
        } catch (e3) {
          try {
            return atob(cleaned.replace(/ /g, '+'));
          } catch (e4) {
            // Strategy 4: If not base64, return as plain string
            return cleaned;
          }
        }
      }
    }
  }

  private parseDecodedText(text: string): QrFieldItem[] {
    const rawLines = text
      .replace(/<br\s*[\/]?>/gi, '\n')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const items: QrFieldItem[] = [];

    for (const line of rawLines) {
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1) {
        const key = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        items.push({ key, value });
      } else {
        items.push({ key: '', value: line });
      }
    }

    return items;
  }

  toggleViewMode(): void {
    this.showRaw = !this.showRaw;
  }

  copyAllToClipboard(): void {
    if (this.fields.length === 0 && !this.decodedText) return;

    const formattedText = this.fields.length > 0
      ? this.fields.map(f => f.key ? `${f.key}: ${f.value}` : f.value).join('\n')
      : this.decodedText;

    this.copyTextToClipboard(formattedText);
    this.copiedAll = true;
    setTimeout(() => {
      this.copiedAll = false;
    }, 2000);
  }

  copyFieldValue(field: QrFieldItem, index: number): void {
    const textToCopy = field.value || field.key;
    this.copyTextToClipboard(textToCopy);
    this.copiedIndex = index;
    setTimeout(() => {
      if (this.copiedIndex === index) {
        this.copiedIndex = null;
      }
    }, 2000);
  }

  private copyTextToClipboard(text: string): void {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private fallbackCopy(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (e) {}
    document.body.removeChild(textArea);
  }

  printRecord(): void {
    window.print();
  }
}
